import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AppraisalTemplateQuestionDto,
  AppraisalTemplateQuestionCreateDto,
  QuestionType
} from '../models';

@Injectable()
export class AppraisalTemplateQuestionService {
  private readonly API_BASE = `${environment.BASE_URL}/appraisal-template-questions`;

  constructor(private http: HttpClient) {}

  /**
   * Get all questions for a section
   * @param sectionId Section ID
   * @returns Observable of question list
   */
  getBySectionId(
    sectionId: number
  ): Observable<AppraisalTemplateQuestionDto[]> {
    if (!sectionId || sectionId <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    return this.http
      .get<
        AppraisalTemplateQuestionDto[]
      >(`${this.API_BASE}/section/${sectionId}`)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific question by ID
   * @param id Question ID
   * @returns Observable of question details
   */
  getById(id: number): Observable<AppraisalTemplateQuestionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid question ID'));
    }

    return this.http
      .get<AppraisalTemplateQuestionDto>(`${this.API_BASE}/${id}`)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Create a new question
   * @param sectionId Section ID
   * @param question Question data
   * @returns Observable of created question
   */
  create(
    sectionId: number,
    question: AppraisalTemplateQuestionCreateDto
  ): Observable<AppraisalTemplateQuestionDto> {
    if (!sectionId || sectionId <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    const validationError = this.validateQuestion(question);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    return this.http
      .post<AppraisalTemplateQuestionDto>(
        `${this.API_BASE}/section/${sectionId}`,
        question
      )
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing question
   * @param id Question ID
   * @param question Updated question data
   * @returns Observable of updated question
   */
  update(
    id: number,
    question: AppraisalTemplateQuestionCreateDto
  ): Observable<AppraisalTemplateQuestionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid question ID'));
    }

    const validationError = this.validateQuestion(question);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    return this.http
      .put<AppraisalTemplateQuestionDto>(`${this.API_BASE}/${id}`, question)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Delete a question
   * @param id Question ID
   * @returns Observable of deletion result
   */
  delete(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid question ID'));
    }

    return this.http.delete(`${this.API_BASE}/${id}`).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Update question sort order within a section
   * @param sectionId Section ID
   * @param questionOrders Array of question IDs in new order
   * @returns Observable of update result
   */
  updateSortOrder(
    sectionId: number,
    questionOrders: { id: number; sortOrder: number }[]
  ): Observable<any> {
    if (!sectionId || sectionId <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    if (!questionOrders || questionOrders.length === 0) {
      return throwError(() => new Error('Question orders are required'));
    }

    return this.http
      .put(`${this.API_BASE}/section/${sectionId}/reorder`, { questionOrders })
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Duplicate a question
   * @param id Question ID to duplicate
   * @returns Observable of duplicated question
   */
  duplicate(id: number): Observable<AppraisalTemplateQuestionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid question ID'));
    }

    return this.http
      .post<AppraisalTemplateQuestionDto>(
        `${this.API_BASE}/${id}/duplicate`,
        {}
      )
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Move a question to a different section
   * @param questionId Question ID
   * @param targetSectionId Target section ID
   * @param sortOrder New sort order
   * @returns Observable of update result
   */
  moveToSection(
    questionId: number,
    targetSectionId: number,
    sortOrder: number
  ): Observable<any> {
    if (!questionId || questionId <= 0) {
      return throwError(() => new Error('Invalid question ID'));
    }

    if (!targetSectionId || targetSectionId <= 0) {
      return throwError(() => new Error('Invalid target section ID'));
    }

    return this.http
      .put(`${this.API_BASE}/${questionId}/move`, {
        targetSectionId,
        sortOrder
      })
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Validate question data
   * @param question Question data to validate
   * @returns Error message if validation fails, null otherwise
   */
  private validateQuestion(
    question: AppraisalTemplateQuestionCreateDto
  ): string | null {
    if (!question) {
      return 'Question data is required';
    }

    if (!question.QuestionText || question.QuestionText.trim() === '') {
      return 'Question text is required';
    }

    if (question.QuestionType === undefined || question.QuestionType === null) {
      return 'Question type is required';
    }

    // Validate question type
    const validTypes = [
      QuestionType.Likert5,
      QuestionType.FreeText,
      QuestionType.YesNo
    ];
    if (!validTypes.includes(question.QuestionType)) {
      return 'Invalid question type';
    }

    // Validate Likert scale labels
    if (question.QuestionType === QuestionType.Likert5) {
      if (!question.LikertLowLabel || question.LikertLowLabel.trim() === '') {
        return 'Likert low label is required for Likert scale questions';
      }
      if (!question.LikertHighLabel || question.LikertHighLabel.trim() === '') {
        return 'Likert high label is required for Likert scale questions';
      }
    }

    // Validate text area rows
    if (question.QuestionType === QuestionType.FreeText) {
      if (question.TextAreaRows <= 0) {
        return 'Text area rows must be greater than 0';
      }
      if (question.MaxLength && question.MaxLength <= 0) {
        return 'Max length must be greater than 0 if specified';
      }
    }

    // Validate visibility and answer permissions
    if (!question.IsEmployeeVisible && !question.IsManagerVisible) {
      return 'Question must be visible to at least one party (employee or manager)';
    }

    if (!question.EmployeeCanAnswer && !question.ManagerCanAnswer) {
      return 'At least one party (employee or manager) must be able to answer the question';
    }

    if (question.EmployeeCanAnswer && !question.IsEmployeeVisible) {
      return 'Employee must be a participant before they can answer the question';
    }

    if (question.ManagerCanAnswer && !question.IsManagerVisible) {
      return 'Manager must be a participant before they can answer the question';
    }

    return null;
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error response
   * @returns Observable error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      if (error.status === 0) {
        errorMessage =
          'Unable to connect to the server. Please check your connection.';
      } else if (error.status === 400) {
        errorMessage = error.error?.message || 'Invalid request data';
      } else if (error.status === 404) {
        errorMessage = 'Question not found.';
      } else if (error.status === 409) {
        errorMessage = error.error?.message || 'A conflict occurred.';
      } else if (error.status === 422) {
        errorMessage =
          error.error?.message || 'Validation failed. Please check your input.';
      } else if (error.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      } else {
        errorMessage = error.error?.message || `Server Error: ${error.status}`;
      }
    }

    console.error(
      'AppraisalTemplateQuestionService Error:',
      errorMessage,
      error
    );
    return throwError(() => new Error(errorMessage));
  }
}
